// src/pages/LoginPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Divider
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import AccountTypeSelector from '../components/AccountTypeSelector';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [popupWindow, setPopupWindow] = useState(null);
  const [showAccountTypeSelector, setShowAccountTypeSelector] = useState(false);
  const [googleData, setGoogleData] = useState(null);
  const location = useLocation();

  const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001';

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const error = params.get('error');
    const message = params.get('message');

    if (error) {
      console.log('Login page error:', {
        error,
        message,
        fullUrl: window.location.href
      });
    }
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await login(formData.email, formData.password);
      console.log('Login response:', response);

      if (response.success) {
        navigate('/dashboard');
      } else {
        setError(response.message || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError(error.response?.data?.message || 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    window.location.href = 'http://localhost:5001/api/auth/google';
  };

  useEffect(() => {
    const handleMessage = async (event) => {
      const { data } = event;
      console.log('Received message:', event);
      
      if (data.type === 'GOOGLE_AUTH_SUCCESS') {
        try {
          console.log('Google auth data:', data);
          
          // Extract and format user data
          const googleData = {
            isGoogleLogin: true,
            userData: {
              email: data.userData.email,
              sub: data.userData.sub,
              given_name: data.userData.given_name,
              family_name: data.userData.family_name,
              name: data.userData.name,
              picture: data.userData.picture,
              email_verified: data.userData.email_verified,
              hd: data.userData.hd
            }
          };
          
          console.log('Attempting Google login with:', googleData);
          const result = await login(googleData);
          
          if (result.success) {
            navigate('/dashboard');
          } else {
            setError(result.message || 'Login failed');
          }
        } catch (error) {
          console.error('Google login error:', error);
          setError(error.response?.data?.message || 'Failed to process Google login');
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [navigate, login, API_URL]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleAccountTypeSelect = async (accountType) => {
    try {
      setIsLoading(true);
      const response = await axios.post(
        `${API_URL}/api/auth/complete-google-registration`,
        {
          ...googleData,
          accountType,
          isActive: true
        },
        {
          headers: {
            Authorization: `Bearer ${googleData.tempToken}`
          }
        }
      );

      if (response.data.success) {
        localStorage.setItem('token', response.data.token);
        login(response.data.user);
        navigate('/dashboard');
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to complete registration');
    } finally {
      setIsLoading(false);
      setShowAccountTypeSelector(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Typography component="h1" variant="h5" align="center">
            Login
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              autoComplete="email"
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="Password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              autoComplete="current-password"
            />
            <Box sx={{ mt: 1, mb: 2 }}>
              <Link 
                to="/forgot-password" 
                style={{ 
                  textDecoration: 'none',
                  color: 'primary.main'
                }}
              >
                <Typography variant="body2" color="primary">
                  Forgot your password?
                </Typography>
              </Link>
            </Box>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoading}
              sx={{ mt: 3, mb: 2 }}
            >
              {isLoading ? 'Logging in...' : 'Login'}
            </Button>
          </Box>

          {/* Google Sign In - Temporarily disabled
          <Divider sx={{ my: 2 }}>OR</Divider>

          <Button 
            onClick={handleGoogleLogin}
            variant="contained"
            startIcon={<GoogleIcon />}
            fullWidth
            sx={{ mt: 2 }}
          >
            Sign in with Google
          </Button>
          */}

          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="textSecondary">
              Don't have an account? <Link to="/register">Register</Link>
            </Typography>
          </Box>
        </Paper>
      </Box>

      <AccountTypeSelector
        open={showAccountTypeSelector}
        onSelect={handleAccountTypeSelect}
        onClose={() => setShowAccountTypeSelector(false)}
        googleData={googleData}
      />
    </Container>
  );
};

export default LoginPage;